import type { TenantNavLink } from "@sagot/constants";
import { usePermissions } from "../../session/permissions";
import { useConsoleNav } from "../../shell/use-console-nav";
import { serviceArea, type ServiceAreaId } from "./areas";
import { SERVICE_AREA_MODULE, useServiceModuleEnabled } from "./service-modules";

export interface ServiceAreaAccess {
  isPending: boolean;
  /** The nav could not be loaded: say so and offer a retry instead of guessing "switched off". */
  isError: boolean;
  retry: () => void;
  /** The server sent the link: the `service_desk` flag is on for the workspace and the member may view it. */
  enabled: boolean;
  /** The workspace plan lacks the page's feature (the server marks the link `locked`). */
  locked: boolean;
}

/** What `GET /v1/nav` says about one Service page: the server alone decides flag, permission and plan. */
export function useServiceAreaAccess(id: ServiceAreaId): ServiceAreaAccess {
  const { entries, isPending, isError, refetch } = useConsoleNav(null);
  const link = entries.find((entry): entry is TenantNavLink => entry.kind === "link" && entry.id === id);
  return { isPending, isError, retry: refetch, enabled: link !== undefined, locked: link?.locked === true };
}

/**
 * Whether a Service page exists for this member in this workspace: its module is switched on, the plan includes it (the
 * server sent the nav link and did not lock it) and the member may view it. Every entry point OUTSIDE the page itself
 * (organisation tabs, ticket context sections, pickers, cross-links) asks this, so a workspace without the module or the plan
 * never sees a way into it. The page's own route answers through `ServiceAreaGuard` with the same three facts.
 */
export function useServiceAreaAvailable(id: ServiceAreaId): boolean {
  const moduleOn = useServiceModuleEnabled(SERVICE_AREA_MODULE[id]);
  const access = useServiceAreaAccess(id);
  const permissions = usePermissions();
  return moduleOn && access.enabled && !access.locked && permissions.can(serviceArea(id).permission, { anywhere: true });
}
